import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError } from "../lib/api";
import { useAuth, type AuthUser } from "../stores/auth";

export const Route = createFileRoute("/login")({
  component: LoginPage,
});

function LoginPage() {
  const nav = useNavigate();
  const setUser = useAuth((s) => s.setUser);
  const queryClient = useQueryClient();
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      const res = await api<{ user: AuthUser }>("/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password: pw }),
      });
      queryClient.setQueryData(["auth", "me"], res.user);
      setUser(res.user);
      await nav({ to: "/" });
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : "login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form
      onSubmit={submit}
      className="mx-auto mt-[max(2rem,env(safe-area-inset-top))] w-[calc(100%-2rem)] max-w-sm space-y-4 rounded-md border border-border/60 p-5 sm:mt-24 sm:space-y-3 sm:p-6"
    >
      <h1 className="text-lg font-semibold">Sign in</h1>
      <label className="block text-sm text-muted sm:text-xs">
        Email
        <input
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 min-h-11 w-full rounded border border-border bg-transparent px-3 py-2 text-base sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm"
        />
      </label>
      <label className="block text-sm text-muted sm:text-xs">
        Password
        <input
          type="password"
          name="password"
          autoComplete="current-password"
          required
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="mt-1 min-h-11 w-full rounded border border-border bg-transparent px-3 py-2 text-base sm:min-h-0 sm:px-2 sm:py-1.5 sm:text-sm"
        />
      </label>
      {err && <div role="alert" className="text-sm text-negRed sm:text-xs">{err}</div>}
      <button
        type="submit"
        disabled={busy}
        className="min-h-11 w-full rounded bg-fg px-3 py-2 text-base font-medium text-bg disabled:opacity-50 sm:min-h-0 sm:bg-accent sm:py-1.5 sm:text-sm sm:font-normal sm:text-white"
      >
        {busy ? "Signing in…" : "Sign in"}
      </button>
    </form>
  );
}
